import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  theme,
} from '@everlywell/ui-kit';
import AddToCartButton from 'components/AddToCartButton';
import MultipleCardsSwiper from 'components/MultipleCardsSwiper';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import PriceV2 from 'components/PricingV2/PriceV2';
import PromoDiscountPrice from 'components/PricingV2/PromoDiscountPrice';
import ProductCardV2 from 'components/ProductCardV2/ProductCardV2';
import { CardTags } from 'components/ProductDisplayGridV2';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useMemo, MouseEventHandler, useRef } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import {
  getActivePromo,
  Promo,
  promoAppliesToProduct,
} from 'utils/helpers/activePromoHelpers';
import useCarouselPadding from 'utils/hooks/useCarouselPadding';
import { ProductDataType } from 'utils/types';
import { CallToAction } from 'utils/types/contentfulTypes';

import {
  trackAddToCartButtonClick,
  trackCardClick,
} from '../ProductCardWithScreener/productCardHelpers';

// Types ----------

export interface ProductCarouselProps {
  productSlugs: string[];
  items?: Queries.ContentfulSection001[];
  title?: string | null;
  description?: string | null;
  eyebrow?: string | null;
  pageTrackingLabel?: string;
  cta?: CallToAction | null;
  advertisedPromos: Promo[];
  showSimplified?: boolean;
}

export const CART_TRACKING_LABEL_SUFIX =
  'Product Carousel - Add To Cart Button Click';
export const CARD_CLICK_TRACKING_LABEL_SUFIX = 'Product Carousel - Card Click';

// ProductCarousel ------

const ProductCarousel: React.FC<ProductCarouselProps> = (props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { carouselPaddingLeft, carouselPaddingRight } =
    useCarouselPadding(containerRef);
  const {
    productSlugs,
    title,
    items = [],
    description,
    eyebrow,
    cta,
    pageTrackingLabel,
    advertisedPromos,
    showSimplified = false,
  } = props;

  const addToCartTrackingLabel = pageTrackingLabel
    ? `${pageTrackingLabel} - ${CART_TRACKING_LABEL_SUFIX}`
    : `${CART_TRACKING_LABEL_SUFIX}`;

  const cardClickTrackingLabel = pageTrackingLabel
    ? `${pageTrackingLabel} - ${CARD_CLICK_TRACKING_LABEL_SUFIX}`
    : `${CARD_CLICK_TRACKING_LABEL_SUFIX}`;

  const productsBySlug: ProductBySlug = useProducts();
  const prodList = productSlugs.map((slug) => productsBySlug[slug]);

  const handleCardClick =
    (product: ProductCardProps): MouseEventHandler<HTMLAnchorElement> =>
    () => {
      trackCardClick(product.slug, cardClickTrackingLabel);
    };

  const handleAddToCart = (product: ProductCardProps) => {
    trackAddToCartButtonClick(product.slug, addToCartTrackingLabel);
  };

  const activePromo = useMemo(
    () => getActivePromo(advertisedPromos),
    [advertisedPromos],
  );

  const getPricingOverride = (product: ProductCardProps): React.ReactNode => (
    <PromoDiscountPrice
      price={product.price}
      promo={activePromo}
      productId={product.productId}
      promotionable={product.promotionable}
      subscribeSelected={false}
      isTextReduced={true}
      isOutOfStock={product.outOfStock}
      promoText={activePromo ? undefined : product.promoText}
      displayCents={true}
    />
  );

  const trackMainCtaClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'Product Carousel',
        ctaText: cta?.label ?? 'No label provided',
      },
    });
  };

  const productCards = prodList
    .filter((product) => product && product.price !== 0)
    .map((product, index: number) => {
      const pdpLink = `/products/${product.slug}/`;

      const productHasPromo =
        activePromo &&
        promoAppliesToProduct(
          product.productId,
          activePromo,
          product.promotionable,
        );

      return (
        <ProductCardV2
          key={`product-card-${index}`}
          size="lg"
          variant="default"
          handleOnClick={handleCardClick(product)}
          data-testid="product-carousel-swiper-card"
          image={
            <>
              <Box display={['block', 'none']} height="100%">
                {product?.boxImage?.gatsbyImageData && (
                  <GatsbyImage
                    image={product?.boxImage?.gatsbyImageData}
                    alt=""
                    imgStyle={{ objectFit: 'contain' }}
                    style={{ height: '100%' }}
                  />
                )}
              </Box>
              <Box display={['none', 'block']} height="100%">
                {product?.productCardImage?.gatsbyImageData && (
                  <GatsbyImage
                    image={product?.productCardImage?.gatsbyImageData}
                    alt=""
                    imgStyle={{ objectFit: 'contain' }}
                    style={{ height: '100%' }}
                  />
                )}
              </Box>
            </>
          }
          pricingOverride={activePromo ? getPricingOverride(product) : null}
          cta={
            <AddToCartButton
              product={product}
              clickAnalyticsData={{
                label: addToCartTrackingLabel,
              }}
              onClick={() => handleAddToCart(product)}
            />
          }
          tag={
            <Flex gap="0.5">
              <CardTags
                product={
                  {
                    onSale: product.onSale,
                    isNew: product.isNew,
                    outOfStock: product.outOfStock,
                    slug: product.slug,
                    price: product.price,
                  } as ProductDataType
                }
                activePromoForProduct={
                  productHasPromo ? activePromo : undefined
                }
              />
            </Flex>
          }
          pricing={
            <>
              <Text
                fontSize="lg"
                color={theme.colors.tints.black}
                fontWeight={'medium'}
              />
              <PriceV2 dollarAmount={(+product.price).toFixed(2)} />
            </>
          }
          linkTo={pdpLink}
        >
          <Text
            fontSize={['2xl', '3xl']}
            color="black"
            fontWeight="medium"
            marginBottom="2"
            textAlign="center"
          >
            {product.displayName}
          </Text>
          <Text
            fontSize="sm"
            color={theme.colors.tints.darkGrey}
            textAlign="center"
          >
            {product.shortDescription}
          </Text>
        </ProductCardV2>
      );
    });

  const itemCards = items.map((item, index: number) => {
    if (!item || !item.images) return null;
    const image = item.images[0]!;

    return (
      <ProductCardV2
        key={`product-card-${index}`}
        size="lg"
        variant="default"
        data-testid="product-carousel-swiper-card"
        image={
          <>
            <Box display="block" height="100%">
              {image.desktopImage?.gatsbyImageData && (
                <GatsbyImage
                  image={image.desktopImage?.gatsbyImageData}
                  alt={image.desktopImageAltText ?? ''}
                  imgStyle={{ objectFit: 'contain' }}
                  style={{ height: '100%' }}
                />
              )}
            </Box>
          </>
        }
        pricingOverride={null}
        pricing={null}
      >
        <Text
          fontSize={['2xl', '3xl']}
          color="black"
          fontWeight="medium"
          marginBottom="2"
          textAlign="center"
        >
          {item?.metadata?.title}
        </Text>
        <Text
          fontSize="md"
          color={theme.colors.tints.darkGrey}
          textAlign="center"
        >
          {item?.metadata?.description?.description}
        </Text>
      </ProductCardV2>
    );
  });

  const carouselWrapperStyles = {
    paddingLeft: carouselPaddingLeft,
    paddingRight: carouselPaddingRight,
  };

  return (
    <SectionContainer data-testid="popularTests" size="fluid">
      <Container
        as="header"
        size="xl"
        width="100%"
        display="flex"
        justifyContent="center"
        flexWrap="wrap"
        marginBottom={5}
        marginLeft="auto"
        marginRight="auto"
        paddingX={[6, 10, 12]}
        ref={containerRef}
      >
        <Text
          size="lg"
          color="viridian.base"
          marginBottom={2}
          width="100%"
          textAlign="center"
        >
          {eyebrow}
        </Text>

        <Heading size="2xl" width="100%" textAlign="center">
          {title}
        </Heading>

        {description && (
          <Box
            display="flex"
            width="100%"
            justifyContent="center"
            marginTop={5}
            marginBottom={6}
            paddingX={[6, 10, 12]}
          >
            <Text
              size="xl"
              color="tints.darkGrey"
              marginBottom={2}
              maxWidth="520px"
              textAlign="center"
            >
              {description}
            </Text>
          </Box>
        )}
      </Container>

      {cta && cta.url ? (
        <Box
          display={['none', 'flex', 'flex']}
          width="100%"
          justifyContent="center"
          marginBottom={6}
        >
          <Button
            variant="primary"
            data-testid="product-carousel-desktop-cta"
            as={Link}
            to={cta.url}
            size="sm"
            onClick={trackMainCtaClick}
          >
            {cta.label}
          </Button>
        </Box>
      ) : null}

      {/* Cards list */}
      {!showSimplified && productCards.length > 0 ? (
        <MultipleCardsSwiper
          cards={productCards}
          carouselStyles={carouselWrapperStyles}
          paginationContainerStyles={carouselWrapperStyles}
          slideStyles={{
            flexShrink: 'unset',
            maxWidth: 'fit-content',
            minWidth: 320,
          }}
          mousewheel={{
            enabled: true,
            forceToAxis: true,
          }}
          freeMode
          breakpointConfig={{
            0: {
              slidesPerView: 'auto',
            },
            900: {
              slidesPerView: 'auto',
            },
            1200: {
              slidesPerView: 'auto',
            },
          }}
        />
      ) : null}

      {showSimplified && itemCards && itemCards.length > 0 ? (
        <MultipleCardsSwiper
          cards={itemCards as React.JSX.Element[]}
          carouselStyles={carouselWrapperStyles}
          paginationContainerStyles={carouselWrapperStyles}
          slideStyles={{
            flexShrink: 'unset',
            maxWidth: 'fit-content',
            minWidth: 320,
          }}
          mousewheel={{
            enabled: true,
            forceToAxis: true,
          }}
          freeMode
          breakpointConfig={{
            0: {
              slidesPerView: 'auto',
            },
            900: {
              slidesPerView: 'auto',
            },
            1200: {
              slidesPerView: 'auto',
            },
          }}
        />
      ) : null}

      {cta && cta.url ? (
        <Box
          width="100%"
          justifyContent="flex-end"
          display={['flex', 'none', 'none']}
          paddingX={[6, 10, 12]}
        >
          <Button
            data-testid="product-carousel-mobile-cta"
            variant="primary"
            as={Link}
            to={cta.url}
            width="100%"
            marginTop="5"
            onClick={trackMainCtaClick}
          >
            {cta.label}
          </Button>
        </Box>
      ) : null}
    </SectionContainer>
  );
};

type ProductCardProps = {
  slug: string;
  outOfStock?: boolean;
  onSale?: boolean;
  isNew?: boolean;
  displayName: string;
  shortDescription: string;
  price: number;
  boxImage: any;
  productCardImage: any;
  productId: number;
  variantId: number;
  promotionable?: boolean;
  promoText?: string;
};

type ProductBySlug = Record<string, ProductCardProps>;

const useProducts = (): ProductBySlug => {
  const { allSpreeProduct, allContentfulProduct } = useStaticQuery(graphql`
    query {
      allContentfulProduct {
        nodes {
          displayName
          slug
          boxImage {
            gatsbyImageData(placeholder: BLURRED)
          }
        }
      }
      allSpreeProduct {
        nodes {
          slug
          promotionable
          variants {
            price
            is_master
            id
          }
          contentfulProductInfo {
            shortDescription
            isNew
            onSale
            outOfStock
            productId
            promoText
            productCardImage {
              gatsbyImageData(placeholder: BLURRED)
            }
          }
        }
      }
    }
  `);

  type ContentfulRecord = {
    displayName: string;
    slug: string;
    boxImage: any;
  };

  const contentfulProductSlugWithName: Record<string, ContentfulRecord> = {};
  // Create a map allContentfulProduct nodes by slug
  allContentfulProduct?.nodes?.forEach((product: any) => {
    if (!product.slug || !product.displayName || !product.boxImage) {
      return;
    }

    contentfulProductSlugWithName[product.slug] = {
      displayName: product.displayName,
      slug: product.slug,
      boxImage: product.boxImage,
    };
  });

  const productsBySlug: ProductBySlug = {};

  allSpreeProduct?.nodes?.forEach((product: any) => {
    const masterPrice = product?.variants?.find(
      (variant: any) => variant?.is_master,
    )?.price;

    const variantId = product?.variants?.find(
      (variant: any) => variant?.is_master,
    )?.id;

    if (
      !masterPrice ||
      !variantId ||
      !product.slug ||
      !product?.contentfulProductInfo?.productCardImage ||
      !product?.contentfulProductInfo?.productId ||
      !contentfulProductSlugWithName[product.slug]
    ) {
      return;
    }

    productsBySlug[product.slug] = {
      slug: product?.slug,
      onSale: product?.contentfulProductInfo?.onSale,
      shortDescription: product?.contentfulProductInfo?.shortDescription,
      outOfStock: product?.contentfulProductInfo?.outOfStock,
      displayName: contentfulProductSlugWithName[product.slug].displayName,
      price: masterPrice,
      boxImage: contentfulProductSlugWithName[product.slug].boxImage,
      productCardImage: product?.contentfulProductInfo?.productCardImage,
      productId: product?.contentfulProductInfo?.productId,
      variantId: variantId,
      promotionable: product?.promotionable,
      promoText: product?.contentfulProductInfo?.promoText,
      isNew: product?.contentfulProductInfo?.isNew,
    };
  });

  return productsBySlug;
};

// Export ---------

export default React.memo(ProductCarousel);
